import React, {useState} from 'react';
import {
    BoxDiv,
    CheckboxDiv,
    ContentWrapper,
    FolderParentName,
    Label,
    ParentRolesWrapper,
    RoleListLabel,
    RolesOnFolder,
    RolesSelectionWrapper,
    SearchFieldWrapper,
} from './AdvancedRolePicker.style';
import {t} from 'i18next';
import {join} from 'lodash';
import {isNullOrEmpty} from '../../utils';

export interface AdvancedRolesSelectionProps {
    readonly dataSource: string[];
    readonly parentRoles: string[];
    readonly userRoles: string[];
    onChange: (userRoles: string[]) => void;
}

const getRolesDataSource = (allRoles: string[], selectedRoles: string[]): string[] => {
    let dataSource = allRoles ? allRoles : [];
    let checkedRoles = selectedRoles ? selectedRoles : [];

    if ((dataSource.length > 0) && (checkedRoles.length > 0)) {
        let roles: string[] = [...checkedRoles.sort()];
        let filteredSources: string[] = dataSource.filter(role => (!checkedRoles.includes(role))).sort();
        if (filteredSources.length > 0) {
            roles = roles.concat(filteredSources);
        }

        return roles;
    }

    return dataSource;
};

/**
 * @name AdvancedRolesPicker
 * @description
 * Component to make a selection of string roles that can describe an item
 */
export const AdvancedRolesPicker: React.VFC<AdvancedRolesSelectionProps> = (props) => {
    const [dataSource, setDataSource] = useState<string[]>(getRolesDataSource(props.dataSource, props.userRoles));
    const [searchRole, setSearchRole] = useState<string>('');
    const [filteredRoles, setFilteredRoles] = useState<string[]>(dataSource);

    const saveRole = (value: string) => {
        let userRoles = props.userRoles.slice();
        let keyIndex = userRoles.indexOf(value);
        if (keyIndex === -1) {
            userRoles.push(value);
        } else {
            userRoles.splice(keyIndex, 1);
        }

        setDataSource(getRolesDataSource(props.dataSource, userRoles));
        props.onChange(userRoles);
    };

    const isSelected = (value: string): boolean => {
        return props.userRoles.includes(value);
    };

    const handleInput = (value: string) => {
        saveRole(value);
    };

    const handleOnChangeSearchRole = (value: string) => {
        let searchRoleValue = value.trim();
        setSearchRole(searchRoleValue);
        if (!isNullOrEmpty(searchRoleValue)) {
            let regex = new RegExp(`${searchRoleValue}`, 'i')
            setFilteredRoles(dataSource.filter(role => regex.test(role)));
        } else {
            setFilteredRoles(dataSource);
        }
    };

    return (
        <RolesSelectionWrapper>
            {(props.parentRoles.length > 0) &&
            <ContentWrapper>
                <ParentRolesWrapper>
                    <FolderParentName>{t('PARENT_ROLE_LABEL_COMPONENT')}</FolderParentName>
                    <RolesOnFolder>{join(props.parentRoles, ', ')}</RolesOnFolder>
                </ParentRolesWrapper>
            </ContentWrapper>
            }
            <ContentWrapper>
                {(dataSource.length === 0) &&
                <Label htmlFor={'no-roles-available'}>{t('ROLES_NOT_AVAILABLE')}</Label>
                }
                {(dataSource.length > 0) &&
                <>
                    <SearchFieldWrapper key='searchField' className='a-text-field a-text-field--search'>
                        <input
                            className='searchInputField roleSearchInputField'
                            type='role-search'
                            id='role-search'
                            placeholder={t('ROLES_SEARCH_TERM')}
                            value={searchRole}
                            onChange={(event) => handleOnChangeSearchRole(event.target.value)}
                        />
                    </SearchFieldWrapper>
                    <RoleListLabel htmlFor={'roles-available'}>{t('ROLES_AVAILABLE')}</RoleListLabel>
                    <BoxDiv className='a-box -floating-shadow-s'>
                        {filteredRoles.map(role => {
                            return <CheckboxDiv key={role} className='a-checkbox'>
                                <input
                                    type='checkbox'
                                    name='unselected Checkbox'
                                    id={role}
                                    checked={isSelected(role)}
                                    onClick={() => handleInput(role)}
                                />
                                <Label htmlFor={role}>{role}</Label>
                            </CheckboxDiv>
                        })}
                    </BoxDiv>
                </>
                }
            </ContentWrapper>
        </RolesSelectionWrapper>
    );
};
